/**
 * This file contains utilities for searching through the recordings.
 */

import {getFormattedDateForSearch} from "../../../../utils/date";
import {PRIORITY_NONE, PRIVACY_PRIVATE, PRIVACY_PUBLIC} from "../../../recording/utils";
import {levenshteinDistance} from "../../../../utils/string";

/**
 * Returns a filtered array based on the following search queries:
 * @param properties  - an object of key-value pairs to be used as an exact match to recordings
 * @param searchString - raw search string, passed from an input text box
 * @param betweenDates - an object of only two or less entries,
 * {@code betweenDates.start} and/or {@code betweenDates.end} to be used as a date based filter
 * @param recordingArray - an array of recordings to be filtered
 */
export const search = (properties, searchString, betweenDates, recordingArray) => {
    if (!properties && !searchString && !betweenDates) {
        return recordingArray;
    }
    let results = recordingArray;
    if (properties && Object.keys(properties).length > 0) {
        results = searchViaProperties(properties, results);
    }
    if (searchString && searchString.length > 0) {
        results = searchViaString(searchString, results);
    }
    if (betweenDates && (betweenDates.start || betweenDates.end)) {
        results = searchViaDates(betweenDates.start, betweenDates.end, results);
    }
    return results;
};

/* -----------------    Searching via properties     ------------------ */

/**
 * Search via properties logic
 * @param properties - an object of key-value pairs to be
 *      used as an exact match to recordings in
 * @param recordingArray - an array of recordings to be filtered
 */
const searchViaProperties = (properties, recordingArray) => {
    const propertyKeys = Object.keys(properties);
    return recordingArray.filter(recording => {
        for (let i = 0; i < propertyKeys.length; i++) {
            const propertyKey = propertyKeys[i];
            if (recording[propertyKey] && recording[propertyKey] === properties[propertyKey]) {
                return true;
            }
        }
        return false;
    });
};

/* -----------------    Searching via String     ------------------ */

/**
 * Search via string URL logic
 * @param searchString - raw search string, passed from an input text box
 * @param recordingArray - an array of recordings to be filtered
 */
const searchViaString = (searchString, recordingArray) => {
    let searchWords = searchString.toLowerCase().trim().split(" ");
    searchWords = searchWords.filter(searchWord => searchWord !== "");
    return recordingArray.filter(recording => {
        for (let i = 0; i < searchWords.length; i++) {
            const word = searchWords[i];
            if (!isWordFoundInTags(word, recording.tags) &&
                !isWordFoundInRecordingTitle(word, recording.title) &&
                !isWordFoundInFormattedDate(word, recording.date) &&
                !isWordFoundInPriority(word, recording.priority) &&
                !isWordReadOrUnread(word, recording.isUnread) &&
                !isWordPrivacyLevel(word, recording.privacy)) {
                return false
            }
        }
        // Default: the recording should belong in the set.
        return true;
    });
};

/**
 * Converts a {@param string} into an object hashSet
 * of keys {@code words} and dummy value {@code true}
 */
const getWordsSet = (string) => {
    const wordArray = string.split(" ");
    let wordObj = {};
    wordArray.map(word => {
        wordObj[word] = true;
        return null;
    });
    return wordObj;
};

/**
 * @returns @type boolean {@code true} if
 * the {@param word} is in
 * the set of {@param tags}
 * and {@code false} otherwise.
 *
 * NOTE: NOT CASE SENSITIVE.
 */
const isWordFoundInTags = (word, tags) => {
    if (!tags || Object.keys(tags).length <= 0) {
        return false;
    }
    return tags[word] || false;
};

/**
 * @returns {@type boolean} {@code true} if
 * the {@param word} is found in the {@param title}
 * and {@code false} otherwise.
 *
 * NOTE: NOT CASE SENSITIVE.
 */
const isWordFoundInRecordingTitle = (word, title) => {
    if (!title) {
        return false;
    }
    const titleWords = getWordsSet(title.toLowerCase());
    return titleWords[word] || false;
};

/**
 * @returns {@type boolean} {@code true} if
 * the {@param word} is in the formatted
 * {@param dateNum}'s words, and
 * {@code false} otherwise.
 *
 * NOTE: NOT CASE SENSITIVE.
 */
const isWordFoundInFormattedDate = (word, dateNum) => {
    if (!dateNum) {
        return false;
    }
    const formattedDateWords = getWordsSet(getFormattedDateForSearch(dateNum));
    return formattedDateWords[word] || false;
};

/**
 * @returns {@type boolean} {@code true} if
 * the {@param word} and the {@param priority}
 * words are equal, and
 * {@code false} otherwise.
 *
 * NOTE: NOT CASE SENSITIVE.
 */
const isWordFoundInPriority = (word, priority) => {
    if (!priority) {
        return word === PRIORITY_NONE || word === "user" || word === "manual";
    } else {
        return word.startsWith(priority) || priority.startsWith(word);
    }
};

/**
 * @returns {@type boolean} {@code true} if
 * the {@param word} {@param isUnread}, and
 * {@code false} otherwise.
 */
const isWordReadOrUnread = (word, isUnread) => {
    if (isUnread === null || isUnread === undefined || isUnread === true) {
        return levenshteinDistance(word, "unread") < 2;
    } else if (isUnread !== null && isUnread !== undefined && isUnread === false) {
        return levenshteinDistance(word, "read") < 2;
    }
    return false;
};

/**
 * @returns {@type boolean} {@code true} if
 * the {@param word} matches the {@param privacy}
 * level of the recording, {@code false} otherwise.
 */
const isWordPrivacyLevel = (word, privacy) => {
    if (privacy === null || privacy === undefined || privacy !== PRIVACY_PUBLIC) {
        return word === PRIVACY_PRIVATE;
    } else if (privacy !== undefined && privacy !== null && privacy === PRIVACY_PUBLIC) {
        return word === PRIVACY_PUBLIC;
    }
    return false;
};

/* -----------------    Searching via Dates     ------------------ */

/**
 * Search via dates logic
 * @param start - date of the filter to begin from (inclusive)
 * @param end - date of the filter to end at (inclusive)
 * @param recordingArray - an array of recordings to be filtered
 */
const searchViaDates = (start, end, recordingArray) => {
    try {
        if (start && end) {
            // Case for both start and end date available
            return recordingArray.filter(recording => {
                return recording.date >= start && recording.date <= end;
            });
        }
        if (start) {
            // Case for only start date available
            return recordingArray.filter(recording => {
                return recording.date >= start;
            });
        }
        if (end) {
            // Case for only end date available
            return recordingArray.filter(recording => {
                return recording.date <= end;
            });
        }
    } catch(error) {
        // Fallback to empty array
        return [];
    }
};



// WEBPACK FOOTER //
// ./src/components/unit/recordings/search/utils.js